import { Link } from "react-router-dom";
import CTA, { Arr } from "../components/CTA";
import Workspace from "../components/Workspace";

const FileIc = () => (
  <svg className="job__file-ic" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M4 1.5h5.5L13 5v9.5H4z" stroke="currentColor" strokeWidth="1.3" />
    <path d="M9.5 1.5V5H13" stroke="currentColor" strokeWidth="1.3" />
  </svg>
);

export default function Home() {
  return (
    <>
      {/* HERO */}
      <section className="hero" data-testid="home-hero">
        <div className="container">
          <div className="hero__grid">
            <div>
              <div className="eyebrow hero__eyebrow reveal">Intelligent Document Processing</div>
              <h1 className="reveal d1">
                Turn document chaos into <span className="accent">structured data</span> you can trust.
              </h1>
              <p className="hero__tagline reveal d2">Trained on your documents. Trusted with your data.</p>
              <p className="hero__lede reveal d2">
                Docket is an intelligent document processing platform you train on your own document types, then it classifies, splits, extracts, validates, summarises and delivers structured data, with an audit trail behind every result.
              </p>
              <div className="hero__actions reveal d3">
                <Link to="/contact" className="btn btn--primary" data-testid="hero-cta-pilot">Request a pilot <Arr /></Link>
                <Link to="/how-it-works" className="btn btn--ghost" data-testid="hero-cta-how">See how it works</Link>
              </div>
              <div className="hero__meta reveal d4">
                <span>Multi-tenant</span>
                <span>Industry-agnostic</span>
                <span>Audit-grade</span>
              </div>
            </div>
            <div className="reveal d2">
              <div className="panel" aria-label="Example of the Docket extraction queue">
                <div className="panel__bar">
                  <span className="panel__dot" /><span className="panel__dot" /><span className="panel__dot" />
                  <span className="panel__title">IDP Extraction</span>
                </div>
                <div className="panel__tabs">
                  <span className="panel__tab active">All <span className="ct">24</span></span>
                  <span className="panel__tab">Action required <span className="ct">3</span></span>
                  <span className="panel__tab">Completed <span className="ct">18</span></span>
                </div>
                <div>
                  <div className="job">
                    <div>
                      <div className="job__name"><FileIc />INV-20418.pdf</div>
                      <div className="job__meta">Invoice model · 1 document</div>
                    </div>
                    <div className="job__right"><span className="pill pill--ok">Ready to send</span><span className="conf conf--ok">98.6%</span></div>
                  </div>
                  <div className="job">
                    <div>
                      <div className="job__name"><FileIc />vendor-batch-07.pdf</div>
                      <div className="job__meta">Split into 3 documents · 11 pages</div>
                    </div>
                    <div className="job__right"><span className="pill pill--info">Split &amp; classified</span><span className="conf conf--info">·</span></div>
                  </div>
                  <div className="job">
                    <div>
                      <div className="job__name"><FileIc />claim_4471.pdf</div>
                      <div className="job__meta">Claims model · needs a reviewer</div>
                    </div>
                    <div className="job__right"><span className="pill pill--warn">Needs review</span><span className="conf conf--warn">71.2%</span></div>
                  </div>
                  <div className="job">
                    <div>
                      <div className="job__name"><FileIc />BOL-88123.pdf</div>
                      <div className="job__meta">Bill of Lading model · delivered</div>
                    </div>
                    <div className="job__right"><span className="pill pill--grey">Stored</span><span className="conf conf--ok">96.1%</span></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BAND */}
      <div className="band">
        <div className="band__inner">
          <span className="band__label">Industry-agnostic by design</span>
          <div className="band__items">
            <span>Logistics</span><span>Finance</span><span>Legal</span><span>Healthcare</span><span>Retail</span>
          </div>
          <Link to="/contact" className="band__cta" data-testid="band-yours-cta" aria-label="Train Docket on your documents">
            <span className="band__cta-plus" aria-hidden="true">+</span>
            <span className="band__cta-text">your documents</span>
            <svg className="band__cta-arr" width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M3 8h9m0 0L8 4m4 4-4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Link>
        </div>
      </div>

      {/* PROBLEM */}
      <section className="section">
        <div className="container">
          <div className="split2">
            <div className="lead-block reveal">
              <div className="eyebrow" style={{ marginBottom: 22 }}>The problem</div>
              <h2>Document work is breaking operations teams.</h2>
              <p>Despite decades of digital transformation, document processing is still one of the most expensive and least visible parts of operations.</p>
              <p className="mark">Teams are stuck between cheap manual data entry and rigid IDP tools that take months to configure.</p>
            </div>
            <div className="plist">
              {[
                ["01", "Skilled people keying data from PDFs", "A large share of operations bandwidth goes to manual entry, work that adds cost but no insight."],
                ["02", "Mixed-document files sorted by hand", "A scanned batch arrives as one file holding many document types. Someone separates them before anything can be processed."],
                ["03", "Errors that compound downstream", "A wrong invoice total or a missed line item becomes everyone's data-quality problem, quietly, and at scale."],
                ["04", "No visibility into what's in flight", "Status lives in chase emails and chat messages, not in the system itself. Nobody can see what's stuck, or why."],
              ].map(([n, h, p], i) => (
                <div key={n} className={`pitem reveal${i ? ` d${i}` : ""}`}>
                  <div className="pitem__no">{n}</div>
                  <div><h3>{h}</h3><p>{p}</p></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WORKSPACE, new app shell mock */}
      <section className="section bg-2 bordered">
        <div className="container">
          <div className="s-head reveal">
            <div className="eyebrow">A single workspace</div>
            <h2>One application for the whole document team.</h2>
            <p className="lede">Training, extraction, classification, review and reporting all live behind a single sidebar, so the person doing the work never has to leave the screen they started on.</p>
          </div>
          <div className="reveal d1">
            <Workspace />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS PREVIEW */}
      <section className="section">
        <div className="container">
          <div className="s-head s-head--center reveal">
            <div className="eyebrow eyebrow--center">How it works</div>
            <h2>One trained pipeline, from upload to delivery.</h2>
            <p className="lede">Docket runs the full document journey on a single platform, and it starts with training, because a platform that has learned your documents is one you can trust with them.</p>
          </div>
          <div className="pipe reveal">
            <div className="pipe__rail" aria-hidden="true" />
            {[
              ["01", "Train", "You teach Docket your document types."],
              ["02", "Classify", "Uploads matched to your trained models."],
              ["03", "Split", "Multi-document files separated cleanly."],
              ["04", "Extract", "Fields and line items, each scored."],
              ["05", "Validate", "Low-confidence work routed to a reviewer."],
              ["06", "Summarise", "A plain-language summary per document."],
              ["07", "Deliver", "Structured data sent where it's needed."],
            ].map(([n, t, d]) => (
              <div key={n} className="pstep"><div className="pstep__node">{n}</div><div className="pstep__name">{t}</div><div className="pstep__desc">{d}</div></div>
            ))}
          </div>
          <div className="reveal" style={{ marginTop: 48, textAlign: "center" }}>
            <Link to="/how-it-works" className="txtlink">Walk through the full pipeline <Arr /></Link>
          </div>
        </div>
      </section>

      {/* PLATFORM PREVIEW */}
      <section className="section bg-2 bordered">
        <div className="container">
          <div className="s-head reveal">
            <div className="eyebrow">The platform</div>
            <h2>Three capabilities that set Docket apart.</h2>
            <p className="lede">Most IDP tools guess. Docket is trained, splits real-world files, and keeps a human in the loop only where it counts.</p>
          </div>
          <div className="capgrid reveal">
            <Link className="cap" to="/platform">
              <div className="cap__ic"><svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="6" r="2.6" stroke="currentColor" strokeWidth="1.6"/><circle cx="6" cy="17" r="2.6" stroke="currentColor" strokeWidth="1.6"/><circle cx="18" cy="17" r="2.6" stroke="currentColor" strokeWidth="1.6"/><path d="M10.3 7.8 7.7 14.4M13.7 7.8l2.6 6.6M8.6 17h6.8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg></div>
              <h3>Document Training</h3>
              <p>Teach Docket your document types through a guided interface, no data scientist, no code required.</p>
            </Link>
            <Link className="cap" to="/platform">
              <div className="cap__ic"><svg viewBox="0 0 24 24" fill="none"><rect x="4" y="3.5" width="9" height="11" rx="1.6" stroke="currentColor" strokeWidth="1.6"/><rect x="11" y="9.5" width="9" height="11" rx="1.6" stroke="currentColor" strokeWidth="1.6" fill="#fff"/><path d="M7 7h3M7 10h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg></div>
              <h3>Document Splitting</h3>
              <p>One file in, the right documents out, boundaries detected, each sub-document classified and routed.</p>
            </Link>
            <Link className="cap" to="/platform">
              <div className="cap__ic"><svg viewBox="0 0 24 24" fill="none"><path d="M12 3l7 2.5v6c0 5-3 8.2-7 9.5-4-1.3-7-4.5-7-9.5v-6z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/><path d="M8.8 11.6l2.2 2.2 4.2-4.8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg></div>
              <h3>Document Validation</h3>
              <p>High-confidence work flows straight through; only flagged fields reach a reviewer, beside the source.</p>
            </Link>
          </div>
          <div className="reveal" style={{ marginTop: 34 }}>
            <Link to="/platform" className="txtlink">See the full platform <Arr /></Link>
          </div>
        </div>
      </section>

      {/* DARK, VISIBILITY */}
      <section className="section dark">
        <div className="container">
          <div className="s-head reveal" style={{ marginBottom: 52 }}>
            <div className="eyebrow eyebrow--light">Visibility</div>
            <h2>See everything. Prove anything.</h2>
            <p className="lede">Dashboards show the pipeline in real time. Reports show what changed. Analytics runs through it all, so the numbers are never more than a glance away.</p>
          </div>
          <div className="dark__grid">
            <div className="dark__points reveal">
              <div className="dpoint"><div className="dpoint__k">Dashboards</div><h3>The operational picture, live</h3><p>KPI tiles, a pipeline distribution bar, and built-in triage, every tile links straight to the action behind it.</p></div>
              <div className="dpoint"><div className="dpoint__k">Reports</div><h3>Period-over-period, automatically</h3><p>Every metric carries its change against the prior equivalent period. When leadership asks "versus last month", the answer is already there.</p></div>
              <div className="dpoint"><div className="dpoint__k">Analytics</div><h3>Confidence scoring as the spine</h3><p>Every field, document and model carries a score, driving routing, retraining, and a short analysis of what to do next.</p></div>
            </div>
            <div className="reveal d1"><DashMock /></div>
          </div>
        </div>
      </section>

      {/* EXPLORE MORE */}
      <section className="section">
        <div className="container">
          <div className="s-head reveal" style={{ marginBottom: 46 }}>
            <div className="eyebrow">Go deeper</div>
            <h2>Three more reasons teams choose Docket.</h2>
          </div>
          <div className="capgrid reveal">
            <Link className="cap" to="/security">
              <div className="cap__ic"><svg viewBox="0 0 24 24" fill="none"><rect x="4" y="10" width="16" height="11" rx="1.8" stroke="currentColor" strokeWidth="1.6"/><path d="M8 10V7a4 4 0 018 0v3" stroke="currentColor" strokeWidth="1.6"/></svg></div>
              <h3>Security &amp; architecture</h3>
              <p>Multi-tenant by design, isolated data, encryption, role-based access, and a tamper-evident audit trail.</p>
            </Link>
            <Link className="cap" to="/industries">
              <div className="cap__ic"><svg viewBox="0 0 24 24" fill="none"><path d="M3 21h18M5 21V8l5-3 5 3v13M15 21V11l4-2v12" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/></svg></div>
              <h3>Industry-agnostic</h3>
              <p>No supported-document list to be left off. If you can show Docket examples, it can learn to process it.</p>
            </Link>
            <Link className="cap" to="/about">
              <div className="cap__ic"><svg viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6"/><path d="M12 7.5v5l3.2 2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg></div>
              <h3>Built by Devnco</h3>
              <p>A software consultancy that has solved document problems for years, and built Docket as the answer.</p>
            </Link>
          </div>
        </div>
      </section>

      <CTA
        title='Put Docket in front of <em>your</em> real documents.'
        text="A typical pilot runs four to six weeks, covers two or three of your document types, and ends with trained models, a measured accuracy baseline, and a clear view of the automation gain."
      />
    </>
  );
}

export function DashMock() {
  return (
    <div className="dash" aria-label="Example of the Docket reporting dashboard">
      <div className="dash__row"><span className="dash__title">Extraction · Overview</span><span className="dash__period">Last 30 days</span></div>
      <div className="kpis">
        <div className="kpi"><div className="kpi__num">8,240</div><div className="kpi__lab">Jobs processed</div><div className="kpi__chg">▲ 12.4%</div></div>
        <div className="kpi"><div className="kpi__num">79%</div><div className="kpi__lab">Auto-approved</div><div className="kpi__chg">▲ 5.1%</div></div>
        <div className="kpi"><div className="kpi__num">312</div><div className="kpi__lab">Needs review</div><div className="kpi__chg down">▼ 8.0%</div></div>
        <div className="kpi"><div className="kpi__num">14</div><div className="kpi__lab">Active models</div><div className="kpi__chg">▲ 2</div></div>
      </div>
      <div className="dash__seg">
        <div className="seg-lab"><span>Pipeline distribution</span><span>8,240 jobs</span></div>
        <div className="segbar"><i /><i /><i /><i /></div>
        <div className="dash__legend">
          <span><i style={{ background: "#3E86B5" }} />Extracted</span>
          <span><i style={{ background: "#5CC5CD" }} />In review</span>
          <span><i style={{ background: "#5FB58C" }} />Delivered</span>
          <span><i style={{ background: "#5C7790" }} />Processing</span>
        </div>
      </div>
    </div>
  );
}
